// Copyright (c) 2020 WiseTime. All rights reserved.

import React, { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useRouteMatch } from "react-router-dom"
import * as UserContextSlice from "features/user-context/slice"
import * as UserContextActions from "features/user-context/actions"
import * as TimelineSlice from "features/timeline/slices/timeline"
import { getProtoEnumNameByValue, notUndefined } from "lib/utils"
import { Authority } from "generated/wt/svc/shared/shared_pb"
import { Membership } from "generated/legebill/site/membership/v4/membership_pb"
import { isFutureDate } from "../timeline/dateTime"
import { tutorialAppCues } from "../timeline/userCues"
import { ROUTE_PATH } from "app/routing"
import { t } from "@lingui/macro"
import { showSnackBar } from "../snackbar/actions"

type SidebarTeam = {
  teamId: string,
  name: string,
  authority: string, // key value of Authority enum
}

const WtSidebar = (): React.ReactElement | null => {
  /*
   * Implementation details:
   * `Properties` and external events from `Dependencies`
   * https://stash.practiceinsight.io/stash/projects/WT/repos/wt-sidebar-frontend/browse/src/components/wt-sidebar/readme.md
   */
  const dispatch = useDispatch()
  const { path } = useRouteMatch()
  const user = useSelector(UserContextSlice.selectUser)
  const memberships = useSelector(UserContextSlice.selectMemberships)
  const areFetchedMemberships = useSelector(UserContextSlice.selectAreFetchedMemberships)
  const teamSitesList = useSelector(UserContextSlice.selectTeamSitesList)
  const currentSiteId = useSelector(UserContextSlice.selectCurrentSiteId)
  const activeDate = useSelector(TimelineSlice.selectActiveDate)
  const activeDateTotalDurationSecs = useSelector(TimelineSlice.selectActiveDateTotalDurationSecs)
  const timeZone = useSelector(TimelineSlice.selectTimeZone)
  const sbPaneRef: { current: HTMLElement & { myTeams: SidebarTeam[] } | null } = React.useRef(null)
  const teams = useMemo((): SidebarTeam[] => (
    memberships
      .map((membership: Membership.AsObject): SidebarTeam | undefined => {
        const teamSite = teamSitesList.find(site => site.id === membership.siteId)
        const authority = getProtoEnumNameByValue(Authority, membership.authority)
        if (teamSite === undefined || authority === undefined) {
          return undefined
        }
        return ({
          teamId: teamSite.id,
          name: teamSite.displayName,
          authority,
        })
      })
      .filter(notUndefined)
  ), [memberships, teamSitesList])

  useEffect(() => {
    if (sbPaneRef.current) {
      sbPaneRef.current.myTeams = teams
    }
  }, [teams])

  const showAddTeamHint: boolean = areFetchedMemberships && memberships.length === 0
  const currentSiteIdChangedCallback = (({ detail: teamId }: CustomEvent<string>) => {
    if (user != null) {
      dispatch(UserContextSlice.setCurrentSiteId({ userId: user.id, siteId: teamId }))
    }
  }) as EventListener
  const isFuture = isFutureDate(activeDate)
  const onShowAppCues = (): void => {
    const noTypeWindow = window as any
    if (!noTypeWindow.Appcues) {
      dispatch(showSnackBar({
        message: t({ id: "msg_tutorial_unavailable", message: "Tutorial is unavailable because your browser has blocked AppCues. Please accept 'Training' cookies or check your ad blocker." }),
        messageType: "ERROR",
        displayContactInfo: false,
      }))
      return
    }
    if (isFuture) {
      // Show tutorial appcue for timeline without 'Add Manual Time' button and toolbar
      noTypeWindow.Appcues.show(tutorialAppCues.FUTURE)
      return
    }
    if (activeDateTotalDurationSecs === 0) {
      // Show tutorial appcue for timeline without toolbar
      noTypeWindow.Appcues.show(tutorialAppCues.EMPTY_DAY)
      return
    }

    noTypeWindow.Appcues.show(tutorialAppCues.FULL)
  }
  const onLogOut = (): void => {
    dispatch(UserContextActions.logOut())
  }

  const setSidebarIsRendered = (): void => {
    dispatch(UserContextSlice.setSidebarIsRendered())
  }

  useEffect(() => {
    window.addEventListener("setSidebarIsRendered", setSidebarIsRendered)
    window.addEventListener("defaultTeamIdChanged", currentSiteIdChangedCallback)
    window.addEventListener("logOut", onLogOut)
    return (): void => {
      window.removeEventListener("setSidebarIsRendered", setSidebarIsRendered)
      window.removeEventListener("defaultTeamIdChanged", currentSiteIdChangedCallback)
      window.removeEventListener("logOut", onLogOut)
    }
  }, [user])

  useEffect(() => {
    window.addEventListener("showAppCuesTutorial", onShowAppCues)
    return (): void => {
      window.removeEventListener("showAppCuesTutorial", onShowAppCues)
    }
  }, [isFuture, activeDateTotalDurationSecs])

  return (
    <wt-sidebar
      full-name={user && `${user.firstName} ${user.lastName}`}
      email={user?.email}
      photo-url={user?.photoUrl}
      default-team-id={currentSiteId}
      active-page={path === ROUTE_PATH.TIMELINE.path ? "TIMELINE" : ""}
      show-add-team-hint={showAddTeamHint}
      show-tutorial-button="true"
      ref={sbPaneRef}
      timezone={timeZone?.name}
    ></wt-sidebar>
  )
}

export default WtSidebar
